import React, { useMemo } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { AnimatePresence } from 'framer-motion';
import dynamic from 'next/dynamic';
import { useRouter } from 'next/router';
import { twMerge } from 'tailwind-merge';
import { faChevronLeft } from '@soundxyz/font-awesome/pro-regular-svg-icons/index.mjs';
import { faUserCircle } from '@soundxyz/font-awesome/pro-solid-svg-icons/index.mjs';
import { Avatar } from '@components/basics/Images/Avatar';
import { HIDDEN_FEATURE_BANNER_ROUTES, HIDDEN_MOBILE_BOTTOM_NAV_ROUTES } from '@components/layout';
import { IconButton } from '@components/main-components/icon-buttons/IconButton';
import { useAuth } from '@context/auth/base';
import { triggerLogin } from '@context/auth/store';
import { useStableCallback } from '@hooks/useStableCallback';
import { useHeaderStore } from '@store/header';
import { useRouterHistory } from '@store/router';
import type { StrokeColor } from '../types';

const DynamicFeedBanner = dynamic(
  () => import('@components/Header/Banner/NewFeedFeature').then(mod => mod.NewFeedFeatureBanner),
  {
    ssr: false,
  },
);

export const MobileHeader = ({ strokeColor }: { strokeColor: StrokeColor }) => {
  const { open, title } = useHeaderStore();
  const { pathname } = useRouter();
  const { authUser, isConnectingAPIUser } = useAuth();

  const openNav = () => open('left-nav');

  const { back, push } = useRouter();
  const { hasNavigated } = useRouterHistory();

  const handleBackClicked = useStableCallback(() => {
    if (hasNavigated) {
      back();
    } else {
      push('/');
    }
  });

  const textColor = useMemo(() => {
    if (strokeColor === 'black') return 'text-black';

    return 'text-white';
  }, [strokeColor]);

  const { showBackButton, showNewFeatureBanner } = useMemo(() => {
    const showBackButton = HIDDEN_MOBILE_BOTTOM_NAV_ROUTES.some(route =>
      pathname.includes(route.path),
    );

    const showNewFeatureBanner = !HIDDEN_FEATURE_BANNER_ROUTES.some(route =>
      pathname.includes(route.path),
    );

    return {
      showNewFeatureBanner,
      showBackButton,
    };
  }, [pathname]);

  return (
    <div className="flex w-full flex-col">
      {showNewFeatureBanner && (
        <AnimatePresence>
          <div className="-mx-4 -mt-4 mb-4 block  md:hidden">
            <DynamicFeedBanner />
          </div>
        </AnimatePresence>
      )}

      <div className="relative flex h-10 w-full items-center justify-center">
        <div className="center absolute left-0 flex h-10">
          {isConnectingAPIUser ? (
            <div className="h-10 w-10 animate-pulse rounded-full bg-base400" />
          ) : showBackButton ? (
            <IconButton
              className="-ml-1.5"
              variant="tertiary"
              icon={<FontAwesomeIcon icon={faChevronLeft} fontSize={27} />}
              onClick={handleBackClicked}
            />
          ) : authUser ? (
            <button onClick={openNav}>
              <Avatar
                size={40}
                walletAddress={authUser.publicAddress}
                webappUri={authUser.webappUri}
                src={authUser.avatar?.url}
                username={authUser.username}
                borderRadius="100%"
                disableLink
              />
            </button>
          ) : (
            <button onClick={triggerLogin} className="h-10 w-10 rounded-full">
              <FontAwesomeIcon
                icon={faUserCircle}
                className={twMerge('text-[40px] md:text-[48px]', textColor)}
              />
            </button>
          )}
        </div>

        <span className={twMerge('font-title text-title-s font-medium', textColor)}>{title}</span>
      </div>
    </div>
  );
};
